import { getIn } from 'formik';
import React, { useContext, useState } from 'react';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { ButtonIconLeft, SecondaryButton } from '../../../../../shared/Button';
import { FieldLabel } from '../../../../../shared/form/FieldLabel';
import { FormContext } from '../../../../../shared/form/Form';
import { ReadOnlyField } from '../../../../../shared/form/ReadOnlyField';
import { spacing03, spacing05 } from '../../../../../styling/spacing';
import { QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import { unitFieldName } from '../../quoteOrDesignFormFieldNameHelpers';
import { getNewRoomHeatLossDetailsFormModel } from '../../rooms/heatLoss/roomHeatLossDetailsFormModel';
import { UnitFieldContext } from '../UnitFieldContext';
import { getInitialUnitHeatLossDetailsFormModel } from './unitHeatLossDetailsFormModel';
import { UnitHeatLossDetailsModal } from './UnitHeatLossDetailsModal';
import { ReactComponent as EyeIcon } from '../../../../../images/icons/eye.svg';
import { ReactComponent as PencilIcon } from '../../../../../images/icons/pencil.svg';

export const UnitHeatLoss = () => {
  const { unit, unitIndex, designCalculations } = assertNotNull(useContext(UnitFieldContext));
  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { buildTypeCode, externalAirTemperatureInDegreesCelsius } = formContext;

  const { setFieldValue, errors } = formContext.formProps;

  const isInReadOnlyMode = useContext(FormContext).isDisabled;

  const [heatLossDetailsModalIsOpen, setHeatLossDetailsModalIsOpen] = useState(false);

  const heatLossDetailsFieldName = unitFieldName(unitIndex, 'heatLossDetails');

  const roomHeatLossDetailsAreInvalid =
    unit.manifolds.filter(
      (manifold, manifoldIndex) =>
        manifold.rooms != null &&
        manifold.rooms.filter((room, roomIndex) =>
          getIn(errors, `units.${unitIndex}.manifolds.${manifoldIndex}.rooms.${roomIndex}.heatLossDetails`),
        ).length > 0,
    ).length > 0;

  const onClickHeatLossDetailsButton = () => {
    if (unit.heatLossDetails == null) {
      const unitHeatLossDetails = getInitialUnitHeatLossDetailsFormModel(
        buildTypeCode,
        externalAirTemperatureInDegreesCelsius.toString(),
        unit,
      );
      setFieldValue(heatLossDetailsFieldName, unitHeatLossDetails);

      unit.manifolds.forEach((manifold, manifoldIndex) => {
        const rooms = manifold.rooms || [];

        rooms.forEach((room, roomIndex) => {
          const roomHeatLossDetails = getNewRoomHeatLossDetailsFormModel(
            room.floorConstruction,
            room.floorLevelCode,
            room.roomType,
            unitHeatLossDetails,
            unit,
            buildTypeCode,
            externalAirTemperatureInDegreesCelsius,
          );
          setFieldValue(
            `units.${unitIndex}.manifolds.${manifoldIndex}.rooms.${roomIndex}.heatLossDetails`,
            roomHeatLossDetails,
          );
        });
      });
    }

    setHeatLossDetailsModalIsOpen(true);
  };

  const hasHeatLossDetails = unit.heatLossDetails != null;

  return (
    <HeatLossContainer>
      <UnitHeatLossDetailsModal
        roomsAreInvalid={roomHeatLossDetailsAreInvalid}
        isOpen={heatLossDetailsModalIsOpen}
        closeModal={() => setHeatLossDetailsModalIsOpen(false)}
      />
      <HeatLossButtonContainer>
        <FieldLabel
          label="Heat Loss"
          fieldName={heatLossDetailsFieldName}
          showError={getIn(errors, heatLossDetailsFieldName) || roomHeatLossDetailsAreInvalid}
          error={
            getIn(errors, heatLossDetailsFieldName) || roomHeatLossDetailsAreInvalid ? 'Details are invalid' : null
          }
        />
        <SecondaryButton onClick={onClickHeatLossDetailsButton}>
          <ButtonIconLeft>{isInReadOnlyMode ? <EyeIcon /> : <PencilIcon />}</ButtonIconLeft>
          {isInReadOnlyMode ? 'View' : !hasHeatLossDetails ? 'Input' : 'Edit'} Heat Loss Details
        </SecondaryButton>
      </HeatLossButtonContainer>
      {hasHeatLossDetails && designCalculations != null && (
        <ReadOnlyField
          label="Total Heat Loss (W)"
          value={
            designCalculations?.inProgress
              ? 'Calculating...'
              : designCalculations?.error != null
              ? 'Error'
              : designCalculations?.response != null
              ? designCalculations.response.heatLossInWatts.toLocaleString()
              : '?'
          }
          showError={designCalculations?.error != null}
          error={
            designCalculations?.error &&
            (designCalculations.error.userVisibleMessage || 'Failed to perform Heat Loss calculations')
          }
        />
      )}
    </HeatLossContainer>
  );
};

const HeatLossContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: ${spacing05};
`;

const HeatLossButtonContainer = styled.div`
  margin-right: ${spacing03};
`;
